import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Form,
  Input,
  Label,
  Row,
  Col,
  InputGroup,
} from "reactstrap";
import { Controller, useForm, useWatch } from "react-hook-form";
import { put, get } from "../../helpers/api_helper";
import { UPDATE_ORDER, NOZZLE_LIST, CUSTOMER_LIST } from "../../helpers/url_helper";
import toastr from "toastr";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";

const EditOrderModal = ({ isOpen, toggle, orderData, onUpdated }) => {
  const {
    handleSubmit,
    control,
    formState: { errors, dirtyFields },
    watch,
    reset,
  } = useForm({
    defaultValues: {
      name: "",
      phone_no: "",
      email: "",
      expected_date_delivery: new Date().toISOString().slice(0, 10),
      shipping_id: "",
      total_qty: "",
      nozzle_ul_id: "",
      price: "",
      vehicle_no: "",
      notes: "",
      status: 1,
      proof: "",
      adjustment_amount: "",
      transfer_customer_ul_id: "",
    },
  });
  const [isFetchingCustomers, setIsFetchingCustomers] = useState(false);
  const [customers, setCustomers] = useState([]);
  useEffect(() => {
    if (orderData) {
      reset({
        ...orderData,
        notes: orderData.notes ?? "", // convert null to ""
        expected_date_delivery:
          orderData.expected_date_delivery?.slice(0, 10) ?? "", // for date safety
        adjustment_amount: orderData.adjustment_amount ?? "",
      });
    }
  }, [orderData, reset]);
  const status = watch("status");
  const [nozzles, setNozzles] = useState([]);

  useEffect(() => {
    const fetchNozzles = async () => {
      try {
        const response = await get(NOZZLE_LIST); // use API helper with URL constant
        setNozzles(response.data.items || []); // adjust if response structure differs
      } catch (error) {
        console.error("Failed to fetch nozzles", error);
      }
    };

    fetchNozzles();
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setIsFetchingCustomers(true);

      // Make API call with large page size to get all customers
      const response = await get(`${CUSTOMER_LIST}?page=1&size=1000`); // Adjust size as needed

      if (!response || !response.data) {
        throw new Error("Invalid API response structure");
      }

      // Handle both possible response structures
      const customersData = response.data.items || response.data;

      if (!Array.isArray(customersData)) {
        throw new Error("Expected array of customers");
      }

      const newCustomers = customersData.map((customer) => ({
        value: customer.ul_id,
        label: `${customer.phone} : ${customer.name}`,
        ...customer,
      }));

      setCustomers(newCustomers);
      toastr.success(`Loaded ${newCustomers.length} customers`);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
      toastr.error("Failed to load customer list");
      setCustomers([]);
    } finally {
      setIsFetchingCustomers(false);
    }
  };

  const onSubmit = async (data) => {
    if (!orderData || !orderData.ul_id) {
      toastr.error("Invalid order data. Cannot update.");
      return;
    }
    // Only include fields that were changed
    const changedFields = Object.keys(dirtyFields).reduce((acc, key) => {
      acc[key] = data[key];
      return acc;
    }, {});

    // Always include status, even if not changed
    const updatedData = {
      ...changedFields,
      status: data.status, // Always include status
      adjustment_amount:
        data.status === 2 && data.adjustment_amount !== ""
          ? parseFloat(data.adjustment_amount)
          : null,
    };

    try {
      await put(UPDATE_ORDER(orderData.ul_id), updatedData);
      toastr.success("Order updated successfully!");
      toggle();
      onUpdated();
    } catch (error) {
      console.error("Error updating order", error);
      toastr.error("Failed to update order.");
    }
  };

  const [dateFlatpickr, setDateFlatpickr] = useState(null);
  const openCalendar = () => {
    if (dateFlatpickr) dateFlatpickr.open();
  };
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Edit Order
        {orderData?.updatedAt && (
          <div className="mb-0 text-muted font-size-12">
            <strong>Last Updated:</strong> {formatDateTime(orderData.updatedAt)}
          </div>
        )}
      </ModalHeader>
      <ModalBody>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col md="6" xl="4">
              <div className="mb-3">
                <Label htmlFor="name">Name</Label>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: "Name is required" }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      id="name"
                      placeholder="Enter name"
                      readOnly
                    />
                  )}
                />
              </div>
            </Col>

            <Col md="6" xl="4">
              <div className="mb-3">
                <Label>Phone</Label>
                <Controller
                  name="phone_no"
                  control={control}
                  rules={{
                    required: "Phone is required",
                    pattern: {
                      value: /^[0-9]*$/,
                      message: "Only numbers allowed",
                    },
                    minLength: {
                      value: 10,
                      message: "Must be 10 digits",
                    },
                    maxLength: {
                      value: 10,
                      message: "Must be 10 digits",
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      readOnly
                      type="tel"
                      maxLength="10"
                      pattern="[0-9]*"
                      inputMode="numeric"
                      onKeyDown={(e) => {
                        if (
                          !/[0-9]|Backspace|ArrowLeft|ArrowRight|Tab|Delete/.test(
                            e.key
                          )
                        ) {
                          e.preventDefault();
                        }
                      }}
                      className={`form-control ${
                        errors.phone_no ? "is-invalid" : ""
                      }`}
                    />
                  )}
                />
                {errors.phone && (
                  <div className="text-danger">{errors.phone.message}</div>
                )}
              </div>
            </Col>
            {/* <Col md="6" xl="4">
              <div className="mb-3">
                <Label>expected delivery date</Label>
                <Controller
                  name="expected_date_delivery"
                  control={control}
                  rules={{
                    required: "Expected delivery date is required",
                  }}
                  render={({ field }) => (
                    <InputGroup>
                      <Flatpickr
                        options={{
                          dateFormat: "Y-m-d",
                          defaultDate: field.value,
                        }}
                        onChange={(selectedDates) => {
                          const date = selectedDates[0];
                          if (date) {
                            field.onChange(date.toISOString().slice(0, 10));
                          }
                        }}
                        className="form-control"
                        placeholder="Select Date"
                      />
                      <div
                        className="input-group-append"
                        onClick={openCalendar}
                        style={{ cursor: "pointer" }}
                      >
                        <span className="input-group-text">
                          <i className="mdi mdi-calendar"></i>
                        </span>
                      </div>
                    </InputGroup>
                  )}
                />
                {errors.expected_date_delivery && (
                  <div className="text-danger">
                    {errors.expected_date_delivery.message}
                  </div>
                )}
              </div>
            </Col> */}

            <Col md="6" xl="4">
              <div className="mb-3">
                <Label>Litre</Label>
                <Controller
                  name="total_qty"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      placeholder="Enter litres"
                      readOnly
                    />
                  )}
                />
              </div>
            </Col>

            <Col md="6" xl="4">
              <div className="mb-3">
                <Label>Nozzle</Label>
                <Controller
                  name="nozel_ul_id"
                  control={control}
                  rules={{ required: "Nozzle is required" }}
                  render={({ field }) => (
                    <Input
                      type="select"
                      {...field}
                      className="form-select"
                      readOnly
                    >
                      <option value="" disabled>
                        Select Nozzle
                      </option>
                      {nozzles.map((nozzle) => (
                        <option
                          key={nozzle.ul_id}
                          value={nozzle.ul_id}
                          selected={nozzle.ul_id === field.value}
                          disabled
                        >
                          {nozzle.vehicle_no} {/* or any label field */}
                        </option>
                      ))}
                    </Input>
                  )}
                />
              </div>
            </Col>

            {/* <Col md="6" xl="4">
              <div className="mb-3">
                <Label>Price</Label>
                <Controller
                  name="price"
                  control={control}
                  render={({ field }) => (
                    <Input {...field} type="text" placeholder="Enter Price" />
                  )}
                />
              </div>
            </Col> */}

            <Col md="6" xl="4">
              <div className="mb-3">
                <Label>Vehicle No</Label>
                <Controller
                  name="vehicle_no"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      readOnly
                      placeholder="Enter Vehicle No"
                    />
                  )}
                />
              </div>
            </Col>

            <Col md="6" xl="4">
              <div className="mb-3">
                <Label>Status</Label>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Input type="select" {...field} className="form-select">
                      <option value="0">Select Status</option>
                      <option value="1">Pending</option>
                      <option value="2">Partially Paid</option>
                      <option value="3">Completed</option>
                      <option value="5">Refund</option>
                    </Input>
                  )}
                />
              </div>
            </Col>
            {status === 2 && (
              <Col md="6" xl="4">
                <div className="mb-3">
                  <Label>
                    Adjust Amount
                    <br />
                    {orderData?.unpaid_amount != null && (
                      <span className="text-muted font-size-14">
                        (Unpaid: ₹
                        {parseFloat(orderData.unpaid_amount).toFixed(2)})
                      </span>
                    )}
                  </Label>
                  <Controller
                    name="adjustment_amount"
                    control={control}
                    rules={{
                      pattern: {
                        value: /^[0-9.]+$/,
                        message: "Please enter a valid Adjustment amount",
                      },
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="Adjustment amount"
                        type="text"
                        inputMode="numeric"
                        maxLength={8}
                        pattern="[0-9.]*"
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*\.?\d{0,2}$/.test(value) || value === "") {
                            field.onChange(value);
                          }
                        }}
                      />
                    )}
                  />
                </div>
              </Col>
            )}
            <Col md="12" xl="4">
              <div className="mb-3">
                <Label>Remarks</Label>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="textarea"
                      placeholder="Enter Remarks"
                    />
                  )}
                />
              </div>
            </Col>
             <Col md="6" xl="4">
              <div className="mb-3">
                <Label>Transfer Orders</Label>
                <Controller
                  name="transfer_customer_ul_id"
                  control={control}
                  render={({ field }) => (
                    <Input
                      type="select"
                      {...field}
                      className="form-select"
                      readOnly
                    >
                      <option value="">
                        Select Customer
                      </option>
                      {customers.map((customer) => (
                        <option
                          key={customer.ul_id}
                          value={customer.ul_id}
                        >
                          {`${customer.name} - ${customer.phone}`} 
                        </option>
                      ))}
                    </Input>
                  )}
                />
              </div>
            </Col>
            <Col md="12" xl="8">
              <div className="mb-3">
                <Label>Proof</Label>
                <Controller
                  name="proof"
                  control={control}
                  render={({ field: { value } }) => {
                    const baseUrl = process.env.REACT_APP_API_URL.replace('/api', '')
                    const imageURL = `${baseUrl}/${value}`;
                    if (!value)
                      return (
                        <span className="text-muted">No Image Available</span>
                      );

                    return (
                      <div style={{ width: "250px", height: "150px" }}>
                        <img
                          src={imageURL}
                          alt="Order Proof"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            cursor: "pointer",
                            border: "1px solid #eee",
                            borderRadius: "4px",
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            const newWindow = window.open();
                            newWindow.document.write(`
                  <html>
                    <head>
                      <title>Order Proof</title>
                      <style>
                        body { margin: 0; display: flex; justify-content: center; align-items: center; height: 100vh; background-color: #f5f5f5; }
                        img { max-width: 100%; max-height: 100%; object-fit: contain; }
                      </style>
                    </head>
                    <body>
                      <img src="${imageURL}" alt="Order Proof" />
                    </body>
                  </html>
                `);
                          }}
                        />
                        <div className="text-muted mt-2 font-size-13">
                          Click to view full image
                        </div>
                      </div>
                    );
                  }}
                />
              </div>
            </Col>
          </Row>

          <div className="d-flex justify-content-end">
            <Button color="secondary" onClick={toggle} className="me-2">
              Cancel
            </Button>
            <Button color="primary" type="submit">
              Update
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default EditOrderModal;
